// Libs
import classNames from 'classnames/bind';
import {
  Box,
  Button,
  IconButton,
  Pagination,
  PaginationProps,
  PaginationRenderItemParams,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
// Others
import { DEFAULT_CURRENT_PAGE, DEFAULT_LIMIT_PAGE } from '~/utils/constants/common';
import { TPaginationStyleType } from '~/utils/type/base';
// Styles, images, icons
import styles from './BasePagination.module.scss';
import { icons } from '~/assets';

type Props = PaginationProps & {
  totalItem?: number;
  rowsPerPage?: number;
  typeStyle?: TPaginationStyleType;
};

const cx = classNames.bind(styles);

const BasePagination = (props: Props) => {
  //#region Destructuring Props
  const {
    count = 0,
    page,
    onChange,
    totalItem = 0,
    rowsPerPage = DEFAULT_LIMIT_PAGE,
    typeStyle = 'circle',
    ...restProps
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base', 'common/main']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_CURRENT_PAGE);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    setCurrentPage(page ? page : DEFAULT_CURRENT_PAGE);
  }, [page]);
  const startItem = (currentPage - 1) * rowsPerPage + 1;
  const endItem = Math.min(currentPage * rowsPerPage, totalItem);
  //#endregion Implement Hook

  //#region Handle Function
  const handlePaginationChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    onChange && onChange(event, page);
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id='basePaginationComponent' className={cx('container')}>
      {!!totalItem && (
        <Typography className={cx('showingItems')}>
          {t('common_showing_items_table', { start: startItem, end: endItem, total: totalItem })}
        </Typography>
      )}

      <Pagination
        color='primary'
        count={count}
        shape='rounded'
        page={currentPage}
        onChange={(event: React.ChangeEvent<unknown>, page: number) => handlePaginationChange(event, page)}
        renderItem={(params: PaginationRenderItemParams) => {
          const { type, disabled, page, selected, onClick } = params;

          if (type === 'previous') {
            return (
              <IconButton disabled={disabled} className={cx('btnAction', 'btnPrevious', typeStyle)} onClick={onClick}>
                <img
                  className={cx('icon')}
                  src={disabled ? icons.commonIconPrevious : icons.commonIconPreviousActive}
                  alt={t('common_alt_img')}
                />
              </IconButton>
            );
          }

          if (type === 'next') {
            return (
              <IconButton disabled={disabled} className={cx('btnAction', 'btnNext', typeStyle)} onClick={onClick}>
                <img
                  className={cx('icon')}
                  src={disabled ? icons.commonIconNext : icons.commonIconNextActive}
                  alt={t('common_alt_img')}
                />
              </IconButton>
            );
          }

          if (type === 'page') {
            return (
              <IconButton className={cx('page', { selected: selected }, typeStyle)} onClick={onClick}>
                {page}
              </IconButton>
            );
          }

          if (type === 'start-ellipsis' || type === 'end-ellipsis') {
            return <IconButton className={cx('page', 'ellipsis', typeStyle)}>...</IconButton>;
          }

          return null;
        }}
        {...restProps}
      />
    </Box>
  );
};

export default BasePagination;
